// External dependencies
import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Internal dependencies
import { uploadImage } from '@/modules/cms/client/api/admin-cms.api';

/**
 * Rich text editor for CMS content.
 *
 * Quill rather than the `TrEditor` used elsewhere in the app: medium-editor's
 * toolbar cannot produce headings, which long-form editorial content needs.
 * Messages and profiles keep TrEditor — this is deliberately scoped to the
 * CMS so the rest of the app is unaffected.
 *
 * The toolbar is deliberately narrower than Quill's defaults. Anything it can
 * produce must survive `cms-sanitize.server.service.js`, and anything that
 * depends on Quill's own stylesheet to render (alignment, colours, sizes,
 * indentation) would look wrong on the public page, which does not load it.
 * Offering a control that silently does nothing is worse than not offering
 * it.
 */

/**
 * Full toolbar, for page bodies.
 *
 * `header: false` is the "normal paragraph" option. h1 is omitted because the
 * page title owns it — the sanitizer demotes any h1 to h2 anyway.
 */
const PAGE_TOOLBAR = [
  [{ header: [2, 3, 4, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  ['clean'],
];

/**
 * Compact toolbar, for announcements — a one-line banner has no use for
 * headings, images or code blocks.
 */
const BANNER_TOOLBAR = [['bold', 'italic', 'underline'], ['link'], ['clean']];

/**
 * Formats Quill is allowed to apply.
 *
 * Kept in step with the toolbars above. Without this, pasted content can
 * carry formats the toolbar does not offer — colours and alignment being the
 * common ones — which then either get stripped server-side or render
 * unstyled.
 */
const PAGE_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'blockquote',
  'code-block',
  'link',
  'image',
];

const BANNER_FORMATS = ['bold', 'italic', 'underline', 'link'];

/**
 * Quill represents "empty" as `<p><br></p>`, which is truthy and would defeat
 * the required-field checks on the server.
 *
 * @param {String} value - HTML from the editor
 * @returns {String} The value, or an empty string when it holds no content
 */
function normalizeEmpty(value) {
  if (typeof value !== 'string') {
    return '';
  }

  const stripped = value
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .trim();

  return stripped === '' ? '' : value;
}

/**
 * Toolbar "image" handler: pick a file, upload it, insert its URL.
 *
 * Quill's default handler inlines the picked file as a `data:` URI, which the
 * server sanitizer strips on save — so the button silently did nothing.
 * Uploading through the CMS endpoint gives a real URL that survives.
 *
 * @param {Object} editorRef - Ref holding the ReactQuill instance
 */
function pickAndInsertImage(editorRef) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/png,image/jpeg,image/gif';

  input.onchange = async () => {
    const [file] = input.files || [];
    if (!file || !editorRef.current) {
      return;
    }

    const quill = editorRef.current.getEditor();
    // Remember where the cursor was: focus moves to the file dialog.
    const range = quill.getSelection(true);

    try {
      const { url } = await uploadImage(file);
      quill.insertEmbed(range.index, 'image', url, 'user');
      quill.setSelection(range.index + 1, 0, 'user');
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message;
      // The editor has no status area of its own; an alert is honest and
      // cannot be missed.
      window.alert(message || 'Failed to upload image.');
    }
  };

  input.click();
}

export default function CmsEditor({
  id,
  value,
  onChange,
  placeholder,
  compact,
}) {
  const editorRef = useRef(null);

  // Quill re-initialises its toolbar whenever `modules` changes identity, so
  // this must not be rebuilt on every render.
  const modules = useMemo(
    () => ({
      toolbar: {
        container: compact ? BANNER_TOOLBAR : PAGE_TOOLBAR,
        handlers: {
          image: () => pickAndInsertImage(editorRef),
        },
      },
      clipboard: {
        // Strip Word/Docs styling on paste rather than carrying it in and
        // having the server remove it after the fact.
        matchVisual: false,
      },
    }),
    [compact],
  );

  const formats = useMemo(
    () => (compact ? BANNER_FORMATS : PAGE_FORMATS),
    [compact],
  );

  return (
    <div
      className={compact ? 'cms-editor cms-editor-compact' : 'cms-editor'}
      id={id}
    >
      <ReactQuill
        ref={editorRef}
        theme="snow"
        value={value || ''}
        onChange={html => onChange(normalizeEmpty(html))}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
}

CmsEditor.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  compact: PropTypes.bool,
};

CmsEditor.defaultProps = {
  value: '',
  compact: false,
};
